import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsScrollEffectMixin from '../../mixins/ids-scroll-effect-mixin/ids-scroll-effect-mixin';
import IdsBox from '../ids-box/ids-box';

import '../ids-toolbar/ids-toolbar';
import styles from './ids-widget.scss';
import type IdsHyperlink from '../ids-hyperlink/ids-hyperlink';
import type IdsHomePage from '../ids-home-page/ids-home-page';

const Base = IdsScrollEffectMixin(
  IdsEventsMixin(
    IdsBox
  )
);

/**
 * IDS widget Component
 * @type {IdsWidget}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsScrollEffectMixin
 * @part body - the body element
 * @part widget - the widget element
 * @part header - the header element
 * @part search - the search header area element
 * @part content - the widget content element
 */
@customElement('ids-widget')
@scss(styles)
export default class IdsWidget extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#setHeight();
    this.#attachEventHandlers();

    // Setup Scroll Effect
    this.scrollArea = this.container?.querySelector<HTMLElement>('.ids-widget-content');
    this.attachScrollEvents();

    // Add classes for height adjustments
    if (this.querySelector('ids-search-field')?.parentElement?.getAttribute('slot') === 'widget-search') {
      this.scrollArea?.classList.add('has-search');
    }

    if (this.querySelector('ids-text[subtitle]')) {
      this.scrollArea?.classList.add('has-subtitle');
      this.container?.querySelector<HTMLElement>('.ids-widget-header')?.classList.add('has-subtitle');
    }

    const header = this.shadowRoot?.querySelector('.ids-widget-header');
    const headerSlot = header?.querySelector<HTMLSlotElement>('slot[name="widget-header"]');
    if (headerSlot?.assignedNodes().length === 0) {
      header?.classList.add('is-empty');
    }

    const footer = this.querySelector('[slot="widget-footer"]');
    this.container?.classList[footer ? 'add' : 'remove']('has-footer');
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.BORDERLESS,
      attributes.HEIGHT,
      attributes.NO_HEADER,
      attributes.OVERFLOW,
      attributes.PADDING_X,
      attributes.PADDING_Y,
      attributes.SIZE,
      attributes.ROWSPAN
    ];
  }

  /**
   * Handle events
   * @private
   * @returns {void}
   */
  #attachEventHandlers() {
    this.offEvent('mouseover.widget');
    this.onEvent('mouseover.widget', this, () => {
      this.querySelectorAll<HTMLButtonElement>('[system-button][hidden]').forEach((elem) => {
        elem.hidden = false;
        elem.setAttribute('ref-hidden', 'true');
      });
    });

    this.offEvent('mouseleave.widget');
    this.onEvent('mouseleave.widget', this, () => {
      this.querySelectorAll<HTMLButtonElement>('[system-button][ref-hidden]').forEach((elem) => {
        if ((elem as any).container.classList.contains('is-active')) {
          if (elem.nextElementSibling?.nodeName === 'IDS-POPUP-MENU') {
            this.onEvent('beforehide.widget', elem.nextElementSibling, () => {
              elem.hidden = true;
              elem.removeAttribute('ref-hidden');
              this.offEvent('beforehide.widget', elem.nextElementSibling);
            });
          }
          return;
        }
        elem.hidden = true;
        elem.removeAttribute('ref-hidden');
      });
    });

    const header = this.shadowRoot?.querySelector('.ids-widget-header');
    const headerSlot = header?.querySelector<HTMLSlotElement>('slot[name="widget-header"]');
    this.offEvent('slotchange.widget-header', headerSlot);
    this.onEvent('slotchange.widget-header', headerSlot, () => {
      if (headerSlot?.assignedNodes().length === 0) {
        header?.classList.add('is-empty');
      } else {
        header?.classList.remove('is-empty');
      }
    });
  }

  /**
   * Method for widget template
   * @returns {string} html
   */
  widgetTemplate() {
    const html = `
      <div class="ids-widget" part="widget">
        <div class="ids-widget-body" part="body">
          <div class="ids-widget-header" part="header">
            <slot name="widget-header"></slot>
          </div>
          <div class="ids-widget-actions" part="actions">
            <slot name="widget-actions"></slot>
          </div>
          <div class="ids-widget-search" part="search">
            <slot name="widget-search"></slot>
          </div>
          <div class="ids-widget-content${this.overflow === ' hidden' ? ' overflow-hidden' : ''}" part="content">
            <slot name="widget-content"></slot>
          </div>
          <div class="ids-widget-footer" part="footer">
            <slot name="widget-footer"></slot>
          </div>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template() {
    return this.widgetTemplate();
  }

  /**
   * Redraw the template when some properties change.
   */
  redraw() {
    if (!this.shadowRoot || !this.container) return;

    const template = document.createElement('template');
    const html = this.template();

    // Render and append styles
    this.shadowRoot.innerHTML = '';
    this.hasStyles = false;
    this.appendStyles();
    template.innerHTML = html;
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    this.container = this.shadowRoot.querySelector<HTMLElement>('.ids-widget');

    if (this.height) {
      const link = this.container?.querySelector<IdsHyperlink>('ids-hyperlink')?.container;
      this.setAttribute(attributes.HEIGHT, this.height);
      this.container?.style.setProperty('height', `${this.height}px`);
      link?.style.setProperty('height', `${this.height}px`);
      this.querySelector('[slot]')?.classList.add('fixed-height');
    }
  }

  /**
   * Set the widget to have no borders and no background color
   * @param {boolean|null} value If widget should be borderless or not
   */
  set borderless(value) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.BORDERLESS, '');
    } else {
      this.removeAttribute(attributes.BORDERLESS);
    }
  }

  get borderless() { return this.hasAttribute(attributes.BORDERLESS); }

  /**
   * Set how the container overflows, can be hidden or auto (default)
   * @param {string | null} [value] css property for overflow
   */
  set overflow(value) {
    if (value === 'hidden') {
      this.container?.querySelector('.ids-widget-content')?.classList.add('overflow-hidden');
      this.setAttribute(attributes.OVERFLOW, value);
    } else {
      this.container?.querySelector('.ids-widget-content')?.classList.remove('overflow-hidden');
      this.removeAttribute(attributes.OVERFLOW);
    }
  }

  get overflow() { return this.getAttribute(attributes.OVERFLOW); }

  /**
   * Set a specific height and center the widget
   * @returns {string} href for ids-hyperlink
   */
  get height() { return this.getAttribute(attributes.HEIGHT) || ''; }

  /**
   * Set a height and center the widget
   * @param {number} height height in pixels
   */
  set height(height) {
    if (height) {
      this.setAttribute(attributes.HEIGHT, height);
    } else {
      this.removeAttribute(attributes.HEIGHT);
    }

    this.#setHeight();
  }

  #setHeight() {
    const linkEl = this.container?.querySelector<IdsHyperlink>('ids-hyperlink')?.container;

    this.container?.style?.setProperty('height', this.height ? `${this.height}px` : '');
    linkEl?.style?.setProperty('height', this.height ? `${this.height}px` : '');
    this.querySelector('[slot]')?.classList.toggle('fixed-height', !!this.height);
  }

  /**
   * Initializes widget dimensions and layout within a homepage context.
   * Calculates and sets widget size based on colspan/rowspan and applies spacing.
   * Only applies when the widget is a direct child of IDS-HOME-PAGE.
   */
  initHomepageWidget() {
    const homepage = (this.parentElement as IdsHomePage);

    if (!homepage.name || homepage?.nodeName !== 'IDS-HOME-PAGE') {
      return;
    }

    const w = this.colspan > 0 ? this.colspan : 1;
    const h = this.rowspan > 0 ? this.rowspan : 1;
    const gaps = homepage.gapValues();
    const widgetHeight = (homepage.widgetHeight * h) + ((gaps.y as number) * (h - 1));
    this.style.width = `${(homepage.widgetWidth * w) + ((gaps.x as number) * (w - 1))}px`;
    this.style.height = `${widgetHeight}px`;
    const headerClasslist = this.container?.querySelector('.ids-widget-body .ids-widget-header')?.classList;
    const hasFooter = this.container?.classList.contains('has-footer');
    const hasSearch = this.scrollArea?.classList.contains('has-search');
    const contentHeight = widgetHeight - (headerClasslist && !headerClasslist.contains('is-empty') ? 56 : 8) - (hasSearch ? 46 : 0) - (hasFooter ? 53 : 0);
    const widgetContent = (this.container?.querySelector('.ids-widget-body div.ids-widget-content') as any);
    if (widgetContent?.style) {
      widgetContent.style.height = `${contentHeight}px`;
    }

    homepage?.refreshGap();
  }

  /**
   * Sets the rowspan attribute for the widget within a homepage.
   * Only applies when the widget is a direct child of IDS-HOME-PAGE.
   * @param {number | string} value - The number of rows the widget should span
   */
  set rowspan(value: number | string) {
    if (this.parentElement?.nodeName !== 'IDS-HOME-PAGE') {
      return;
    }

    this.setAttribute(attributes.ROWSPAN, `${value}`);
    this.initHomepageWidget();
  }

  /**
   * Gets the rowspan value of the widget. Defaults to 1 if not set.
   * @returns {number} The current rowspan value
   * @private
   */
  get rowspan(): number {
    return +(this.getAttribute(attributes.ROWSPAN) || '1');
  }

  /**
   * Gets the colspan value of the widget. Defaults to 1 if not set.
   * @returns {number} The current colspan value
   * @private
   */
  get colspan() {
    return +(this.getAttribute(attributes.COLSPAN) || '1');
  }

  /**
   * Set to true to hide the header and reclaim the space
   * @returns {boolean} target for ids-hyperlink
   */
  get noHeader() { return this.getAttribute(attributes.NO_HEADER); }

  set noHeader(value) {
    if (value) {
      this.setAttribute(attributes.NO_HEADER, value);
    } else {
      this.removeAttribute(attributes.NO_HEADER);
    }
  }

  /**
   * Set the x axis padding on the widget contents (in pixels)
   * @param {string | null} value The value of the paddingX attribute
   */
  set paddingX(value: string | null) {
    if (!value) {
      this.removeAttribute(attributes.PADDING_X);
      this.container?.querySelector<HTMLElement>('.ids-widget-content')?.style.removeProperty('padding-block');
    } else {
      this.setAttribute(attributes.PADDING_X, value);
      this.container?.querySelector<HTMLElement>('.ids-widget-content')?.style.setProperty('padding-block', `${value}px`);
    }
  }

  /**
   * Get the x axis padding
   * @returns {string | null} The number value that represents the paddingX of the widget contents
   */
  get paddingX(): string | null { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the y axis padding on the widget contents (in pixels)
   * @param {string | null} value The value of the paddingY attribute
   */
  set paddingY(value: string | null) {
    if (!value) {
      this.removeAttribute(attributes.PADDING_Y);
      this.container?.querySelector<HTMLElement>('.ids-widget-content')?.style.removeProperty('padding-inline');
    } else {
      this.setAttribute(attributes.PADDING_Y, value);
      this.container?.querySelector<HTMLElement>('.ids-widget-content')?.style.setProperty('padding-inline', `${value}px`);
    }
  }

  /**
   * Get the x axis padding
   * @returns {string | null} The number value that represents the paddingY of the widget contents
   */
  get paddingY(): string | null { return this.getAttribute(attributes.PADDING_Y); }

  /**
   * Set the size of the widget (can be half or quarter)
   * @param {'quarter' | 'half' | null} value The value size attribute
   */
  set size(value: 'quarter' | 'half' | null) {
    if (value) {
      this.setAttribute(attributes.SIZE, value);
    } else {
      this.removeAttribute(attributes.SIZE);
    }
  }

  /**
   * Get the current size of the widget
   * @returns {'quarter' | 'half' | null} The value of the size attribute
   */
  get size(): 'quarter' | 'half' | null { return this.getAttribute(attributes.SIZE) as 'quarter' | 'half' | null; }
}
